import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
// COMPONENTE
import ZonaCargando from "../alertas/zonaCargando";
import MENSAJES_PRESETS from "../../componentesEspeciales/soporteAdmin/mensajesPresets";



function Conversacion(){

    const location = useLocation();
    const navigate = useNavigate();
    const { usuario, cargandoUsuario } = useOutletContext() || {};
    const idMensaje = location?.state?.params?.idMensaje || "NA";

    const [cargando, setCargando] = useState(false);
    const [conversacion, setConversacion] = useState(null);
    const [respuesta, setRespuesta] = useState("");
    const [mostrarPresets, setMostrarPresets] = useState(false);
    const cargandoFinal = Boolean(cargandoUsuario) || Boolean(cargando);

    const chatRef = useRef(null);
    const pollingRef = useRef(null);

    function formatearFecha(fecha) {
        if (!fecha) return "Sin fecha";

        try {
            return new Date(fecha).toLocaleString("es-CO", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch (err) {
            return "Sin fecha";
        }
    }

    function esMensajeCliente(mensaje) {
        return mensaje?.direccion === "entrante" || mensaje?.enviadoPor === "Cliente";
    }

    function etiquetaEstado(estado) {
        const estados = {
            esperando_asesor: "Esperando asesor",
            abierta: "Abierta",
            pendiente: "Pendiente",
            resuelta: "Resuelta",
            cerrada: "Cerrada",
        };

        return estados[estado] || estado || "Sin estado";
    }

    async function fetchConversacion({ inicial = false } = {}) {
        if (!usuario || !idMensaje || idMensaje === "NA") return;

        try {
            if (inicial) setCargando(true);

            const res = await axios.post("/obtener-conversacion", {
                params: { idMensaje }
            });

            setConversacion(res?.data?.conversacion || null);
        }
        catch (err) {
            if (inicial) {
                console.log("Error obteniendo conversación de WhatsApp:", err);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "No pudimos obtener la conversación.",
                    timer: 1500,
                    showConfirmButton: false,
                    customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' }
                });
            }
        }
        finally {
            if (inicial) setCargando(false);
        }
    }

    // Carga inicial + polling cada 5 s mientras el chat esté activo
    useEffect(() => {
        fetchConversacion({ inicial: true });

        pollingRef.current = setInterval(() => {
            fetchConversacion({ inicial: false });
        }, 5000);

        return () => clearInterval(pollingRef.current);
    }, [usuario, idMensaje]);

    // Detener polling cuando la conversación quede resuelta o cerrada
    useEffect(() => {
        if (conversacion?.estado === "resuelta" || conversacion?.estado === "cerrada") {
            clearInterval(pollingRef.current);
        }
    }, [conversacion?.estado]);

    // Auto-scroll al último mensaje cuando lleguen mensajes nuevos
    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [conversacion?.mensajes?.length]);


    async function enviarRespuesta(e) {
        e?.preventDefault?.();

        const mensajeLimpio = respuesta.trim();

        if (!mensajeLimpio || !conversacion?.idConversacion) return;

        try {
            setCargando(true);

            const res = await axios.post("/responder-conversacion", {
                params: {
                    idMensaje: conversacion.idConversacion,
                    mensaje: mensajeLimpio,
                    asesor: usuario,
                }
            });

            if (res?.data?.exito) {
                setConversacion(res?.data?.conversacion || conversacion);
                setRespuesta("");
                if (res?.data?.ventana24hExpirada) {
                    Swal.fire({
                        icon: "warning",
                        title: "Mensaje no entregado",
                        text: "El cliente no ha escrito en más de 24 horas. WhatsApp no permite enviar mensajes hasta que el cliente escriba primero.",
                        confirmButtonText: "Entendido",
                        customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar' }
                    });
                }
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: res?.data?.mensaje || "No pudimos enviar la respuesta.",
                    timer: 1500,
                    showConfirmButton: false,
                    customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' }
                });
            }
        }
        catch (err) {
            console.log("Error enviando respuesta de WhatsApp:", err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: err?.response?.data?.mensaje || "No pudimos enviar la respuesta.",
                timer: 1500,
                showConfirmButton: false,
                customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' }
            });
        }
        finally {
            setCargando(false);
        }
    }

    async function finalizarConversacion() {
        if (!conversacion?.idConversacion) return;

        const confirmar = await Swal.fire({
            icon: "question",
            title: "¿Finalizar chat?",
            text: "La conversación pasará a finalizados y dejará de aparecer como chat activo.",
            showCancelButton: true,
            confirmButtonText: "Sí, finalizar",
            cancelButtonText: "Cancelar",
            customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' }
        });

        if (!confirmar.isConfirmed) return;

        try {
            setCargando(true);

            const res = await axios.post("/finalizar-conversacion", {
                params: {
                    idMensaje: conversacion.idConversacion,
                    asesor: usuario,
                }
            });

            if (res?.data?.exito) {
                setConversacion(res?.data?.conversacion || conversacion);
                Swal.fire({
                    icon: "success",
                    title: "Chat finalizado",
                    text: "La conversación fue marcada como resuelta.",
                    timer: 1400,
                    showConfirmButton: false,
                    customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' }
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: res?.data?.mensaje || "No pudimos finalizar la conversación.",
                    timer: 1500,
                    showConfirmButton: false,
                    customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' }
                });
            }
        }
        catch (err) {
            console.log("Error finalizando conversación de WhatsApp:", err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: err?.response?.data?.mensaje || "No pudimos finalizar la conversación.",
                timer: 1500,
                showConfirmButton: false,
                customClass: { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' }
            });
        }
        finally {
            setCargando(false);
        }
    }

    return (
        <>
            {cargandoFinal ? (
                <ZonaCargando />
            ) : null}

            <div className="container-fluid py-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <button 
                        type="button" 
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                        onClick={() => navigate(-1)}
                    >
                        <i className="bi bi-arrow-left me-1"></i>
                        Volver
                    </button>
                </div>

                {!conversacion ? (
                    <div className="border rounded-4 p-4 bg-light text-center">
                        <i className="bi bi-chat-dots fs-2 text-muted"></i>
                        <h1 className="h6 mt-2 mb-1 text-dark">Conversación no encontrada</h1>
                        <p className="text-muted small mb-0">No pudimos cargar el chat solicitado.</p>
                    </div>
                ) : (
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="card-header bg-white border-bottom p-3">
                            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48 }}>
                                        <i className="bi bi-whatsapp fs-4"></i>
                                    </div>

                                    <div>
                                        <h1 className="h5 mb-1 text-dark">
                                            {conversacion?.nombreWhatsapp && conversacion.nombreWhatsapp !== "NA" ? conversacion.nombreWhatsapp : "Usuario de WhatsApp"}
                                        </h1>
                                        <div className="text-muted small">
                                            <i className="bi bi-phone me-1"></i>{conversacion?.telefonoWhatsapp || conversacion?.waId || "Sin teléfono"}
                                            <span className="mx-2">•</span>
                                            <span>{conversacion?.tipoUsuario || "desconocido"}</span>
                                        </div>
                                        {(conversacion?.usuarioData || conversacion?.contactoReportado) && (
                                            <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                                                {conversacion.usuarioData?.username && (
                                                    <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis border border-primary-subtle" style={{ fontSize: "0.72rem" }}>
                                                        <i className="bi bi-person me-1"></i>@{conversacion.usuarioData.username}
                                                    </span>
                                                )}
                                                {conversacion.usuarioData?.email && (
                                                    <span className="badge rounded-pill bg-light text-muted border" style={{ fontSize: "0.72rem" }}>
                                                        <i className="bi bi-envelope me-1"></i>{conversacion.usuarioData.email}
                                                    </span>
                                                )}
                                                {!conversacion.usuarioData && conversacion.contactoReportado && (
                                                    <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis border border-warning-subtle" style={{ fontSize: "0.72rem" }}>
                                                        <i className="bi bi-envelope me-1"></i>{conversacion.contactoReportado}
                                                        <span className="ms-1 opacity-75">(no encontrado)</span>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis border border-primary-subtle px-3 py-2">
                                        {etiquetaEstado(conversacion?.estado)}
                                    </span>

                                    {conversacion?.usuarioData?.username && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                            onClick={() => {
                                                localStorage.setItem("admin_nav_usuario", JSON.stringify({ username: conversacion.usuarioData.username }));
                                                window.open("/admin/usuario", "_blank");
                                            }}
                                        >
                                            <i className="bi bi-person-badge me-1"></i>
                                            Ver usuario
                                        </button>
                                    )}
                                    {conversacion?.usuarioData?.username && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                            onClick={() => {
                                                localStorage.setItem("admin_nav_servicios_usuario", JSON.stringify({ query: conversacion.usuarioData.username }));
                                                window.open("/admin/servicios-por-usuarios", "_blank");
                                            }}
                                        >
                                            <i className="bi bi-briefcase me-1"></i>
                                            Servicios
                                        </button>
                                    )}

                                    {conversacion?.estado !== "resuelta" && conversacion?.estado !== "cerrada" ? (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                            onClick={finalizarConversacion}
                                            disabled={cargando}
                                        >
                                            <i className="bi bi-check-circle me-1"></i>
                                            Finalizar chat
                                        </button>
                                    ) : null}

                                    <span className="badge rounded-pill bg-light text-muted border px-3 py-2">
                                        {conversacion?.mensajes?.length || 0} mensajes
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="card-body p-0">
                            <div ref={chatRef} className="p-3 p-lg-4" style={{ background: "var(--color-bg-surface-alt)", minHeight: "58vh", maxHeight: "68vh", overflowY: "auto" }}>
                                {(conversacion?.mensajes || []).map((mensaje) => {
                                    const cliente = esMensajeCliente(mensaje);

                                    return (
                                        <div 
                                            key={mensaje?._id || mensaje?.idMensaje} 
                                            className={`d-flex mb-3 ${cliente ? "justify-content-start" : "justify-content-end"}`}
                                        >
                                            <div 
                                                className={`rounded-4 px-3 py-2 shadow-sm ${cliente ? "bg-white text-dark" : "bg-primary text-white"}`}
                                                style={{ maxWidth: "72%" }}
                                            >
                                                <div className={`small fw-semibold mb-1 ${cliente ? "text-muted" : "text-white-50"}`}>
                                                    {mensaje?.enviadoPor || (cliente ? "Cliente" : "Soporte")}
                                                </div>

                                                <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                                    {mensaje?.mensaje || "Mensaje sin texto"}
                                                </div>

                                                <div className={`text-end small mt-2 ${cliente ? "text-muted" : "text-white-50"}`}>
                                                    {formatearFecha(mensaje?.timestampWhatsapp || mensaje?.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="card-footer bg-white border-top p-3">
                            <div className="mb-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-muted p-0 text-decoration-none"
                                    onClick={() => setMostrarPresets((v) => !v)}
                                >
                                    <i className={`bi bi-lightning${mostrarPresets ? "-fill text-warning" : ""} me-1`}></i>
                                    Respuestas rápidas
                                </button>

                                {mostrarPresets && (
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {MENSAJES_PRESETS.map((preset, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary rounded-pill"
                                                onClick={() => {
                                                    setRespuesta(preset.texto);
                                                    setMostrarPresets(false);
                                                }}
                                            >
                                                {preset.etiqueta}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <form className="d-flex gap-2 align-items-center" onSubmit={enviarRespuesta}>
                                <input
                                    type="text"
                                    className="form-control rounded-pill"
                                    placeholder="Escribe una respuesta para el cliente..."
                                    value={respuesta}
                                    onChange={(e) => setRespuesta(e.target.value)}
                                    disabled={cargando}
                                />
                                <button
                                    className="btn btn-primary rounded-pill px-4"
                                    type="submit"
                                    disabled={cargando || !respuesta.trim()}
                                >
                                    Enviar
                                </button>
                            </form>
                            <p className="text-muted small mb-0 mt-2">
                                La respuesta se enviará al WhatsApp del cliente y quedará guardada en esta conversación.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}


export default Conversacion;